import { useCallback, useEffect, useMemo } from "react";
import { Download, FolderInput, FolderLock, HardDrive, Loader2, Mail, MailOpen, Menu, Paperclip, RefreshCw, Trash2, X } from "lucide-react";
import { InboxMessageRow } from "./InboxMessageRow";
import { InboxMessageRowExitWrap } from "./InboxMessageRowExitWrap";
import { InboxListFilterSelect } from "./InboxListFilterSelect";
import { MailLoadingState } from "@/features/shared/MailLoadingState";
import { MailCircularProgress } from "@/features/shared/MailCircularProgress";
import { MailTooltip } from "@/features/shared/MailTooltip";
import { useInboxBodyCacheFlags, prefetchInboxBodyIfCached } from "@/hooks/useInboxBodyCacheFlags";
import { useMultiFolderBodyCacheFlags } from "@/hooks/useMultiFolderBodyCacheFlags";
import { useMultiAccountBodyCacheFlags } from "@/hooks/useMultiAccountBodyCacheFlags";
import { useInboxPreparingMessages } from "@/hooks/useInboxPreparingMessages";
import { queueWarmMessageBodies } from "@/lib/mail/bodyWarmup";
import {
  isClientHydrateSettled,
  schedulePrimeVisibleReadyBodies,
} from "@/lib/mail/primeVisibleReadyBodies";
import { warmMessageBodyForOpen } from "@/lib/mail/bodyCacheLookup";
import { isMessageBodyCached } from "@/lib/mail/messageBodyCacheStatus";
import { INBOX_CLIENT_HYDRATE_MAX } from "@/lib/constants/inbox";
import { isBodyCacheOpenable } from "@/features/inbox/MessageBodyCacheBadge";
import { formatSnoozeUntil } from "@/lib/snooze/snoozeDateTime";
import { unifiedMessageKey } from "@/lib/unified/unifiedInboxMerge";
import { inboxFilterIsActive, type InboxListFilter } from "@/lib/mail/messageLabels";
import { navFolderSlot } from "@/lib/mail/folderNavSections";
import type { MailMessageSummary } from "@/lib/types/mail";
import { cn } from "@/lib/cn";

type Props = {
  navigationOpen: boolean;
  folderLabel: string;
  accountEmail?: string;
  accountId?: number;
  folder?: string;
  messages: MailMessageSummary[];
  total: number;
  syncing?: boolean;
  loadingInitial?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  error: string | null;
  fromCache?: boolean;
  selectedUid: number | null;
  contextMenuUid?: number | null;
  checkedUids?: ReadonlySet<number>;
  exitingUids?: ReadonlySet<number>;
  listFilter?: InboxListFilter;
  hiddenByFilterCount?: number;
  showCheckboxes?: boolean;
  onRefresh: () => void;
  onLoadMore: () => void;
  onSelectMessage: (
    uid: number,
    event?: React.MouseEvent,
    options?: { bodyBlocked?: boolean; accountId?: number }
  ) => void;
  onToggleCheck?: (uid: number, event?: React.MouseEvent, accountId?: number) => void;
  onContextMenu?: (event: React.MouseEvent, uid: number, accountId?: number) => void;
  onListFilterChange?: (patch: Partial<InboxListFilter>) => void;
  onBulkAction?: (actionId: string) => void;
  onClearSelection?: () => void;
  onOpenNav?: () => void;
  searchQuery?: string;
  searching?: boolean;
  searchPending?: boolean;
  /** IMAP en cours (requête commitée). */
  searchImapLoading?: boolean;
  searchLocalCount?: number;
  /** true seulement quand IMAP a répondu sans résultat. */
  searchShowEmptyResults?: boolean;
  searchCommittedQuery?: string;
  onPrefetchMessage?: (uid: number) => void;
  /** Bloque l’ouverture tant que le corps n’est pas prêt (pastille verte). */
  requireBodyReady?: boolean;
  /** Brouillons : pas de badge nuage ni blocage cache corps. */
  isDraftsFolder?: boolean;
  /** Envoyés : pas de spinner warm bloquant (corps chargé à l’ouverture). */
  isSentFolder?: boolean;
  /** Envoi programmé : liste vide dédiée. */
  isOutboxFolder?: boolean;
  hideBulkMove?: boolean;
  selectionCount?: number;
  /** UID → dossier IMAP pour pastilles corps (vue En attente). */
  bodyFolderByUid?: Map<number, string>;
  /** UID liste → UID IMAP corps (hold ≠ origine). */
  bodyUidByUid?: Map<number, number>;
  /** UID → échéance ISO pour affichage report. */
  snoozeUntilByUid?: Map<number, string>;
  /** Sélection par ligne (ex. réception unifiée, UIDs dupliqués). */
  isRowSelected?: (msg: MailMessageSummary) => boolean;
  isRowChecked?: (msg: MailMessageSummary) => boolean;
  isContextMenuRow?: (msg: MailMessageSummary) => boolean;
  accountLabelForMessage?: (msg: MailMessageSummary) => string | undefined;
  accountIdForMessage?: (msg: MailMessageSummary) => number | undefined;
  listSubtitle?: string;
  /** Ouvre le téléchargement stockage local du dossier (Phase 2). */
  onOpenFolderStorage?: () => void;
  folderStorageActive?: boolean;
};

export function InboxList({
  navigationOpen,
  folderLabel,
  accountEmail,
  accountId,
  folder = "INBOX",
  messages,
  total,
  syncing = false,
  loadingInitial = false,
  loadingMore,
  hasMore,
  error,
  fromCache: _fromCache,
  selectedUid,
  contextMenuUid = null,
  checkedUids,
  exitingUids,
  listFilter,
  hiddenByFilterCount = 0,
  showCheckboxes = false,
  onRefresh,
  onLoadMore,
  onSelectMessage,
  onToggleCheck,
  onContextMenu,
  onListFilterChange,
  onBulkAction,
  onClearSelection,
  onOpenNav,
  searchQuery = "",
  searching = false,
  searchPending = false,
  searchImapLoading = false,
  searchLocalCount = 0,
  searchShowEmptyResults = false,
  searchCommittedQuery = "",
  onPrefetchMessage,
  requireBodyReady = false,
  isDraftsFolder = false,
  isSentFolder = false,
  isOutboxFolder = false,
  hideBulkMove = false,
  selectionCount,
  bodyFolderByUid,
  bodyUidByUid,
  snoozeUntilByUid,
  isRowSelected,
  isRowChecked,
  isContextMenuRow,
  accountLabelForMessage,
  accountIdForMessage,
  listSubtitle,
  onOpenFolderStorage,
  folderStorageActive = false,
}: Props) {
  const showSearchHint = searching && searchQuery.trim().length > 0;
  const searchDebouncePending = showSearchHint && searchPending;
  const searchImapPending = showSearchHint && searchImapLoading;
  const initialLoading =
    (syncing || loadingInitial) &&
    messages.length === 0 &&
    !searchImapPending &&
    !searchDebouncePending;
  const backgroundSync = syncing && messages.length > 0 && !searchImapPending;
  const bulkCount = selectionCount ?? checkedUids?.size ?? 0;
  const bulkActive = bulkCount > 0;

  const listUids = useMemo(
    () => messages.filter((m) => m.uid > 0).map((m) => m.uid),
    [messages]
  );

  const listBodyCached = useMemo(() => {
    const map = new Map<number, boolean>();
    for (const msg of messages) {
      // Truthy : PHP / merges peuvent envoyer 1 plutôt que true.
      if (msg.uid > 0 && msg.bodyCached) {
        map.set(msg.uid, true);
      }
    }
    return map;
  }, [messages]);

  const priorityBodyUids = useMemo(
    () => messages.filter((m) => m.uid > 0 && !m.seen).map((m) => m.uid),
    [messages]
  );

  const isDraftsView =
    isDraftsFolder || navFolderSlot(folder, folderLabel) === "drafts";
  const isSentView = isSentFolder || navFolderSlot(folder, folderLabel) === "sent";

  const useMultiAccountBody = accountIdForMessage != null;

  const useMultiBodyFlags = !useMultiAccountBody && bodyFolderByUid != null && bodyFolderByUid.size > 0;

  const multiAccountBodyEntries = useMemo(
    () =>
      useMultiAccountBody
        ? messages
            .filter((m) => m.uid > 0 && (m.accountId != null || accountIdForMessage?.(m) != null))
            .map((m) => ({
              accountId: (m.accountId ?? accountIdForMessage!(m)) as number,
              uid: m.uid,
              folder: m.imapFolder ?? folder ?? "INBOX",
              bodyCached: m.bodyCached,
            }))
        : [],
    [accountIdForMessage, folder, messages, useMultiAccountBody]
  );

  const bodyCacheEntries = useMemo(
    () =>
      messages
        .filter((m) => m.uid > 0)
        .map((m) => {
          const bodyUid = bodyUidByUid?.get(m.uid) ?? m.imapBodyUid ?? m.uid;
          return {
            listUid: bodyUid !== m.uid ? m.uid : undefined,
            uid: bodyUid,
            folder: bodyFolderByUid?.get(m.uid) ?? m.imapFolder ?? folder ?? "INBOX",
            bodyCached: m.bodyCached,
          };
        }),
    [bodyFolderByUid, bodyUidByUid, folder, messages]
  );

  const bodyCacheFlagsMultiAccount = useMultiAccountBodyCacheFlags(multiAccountBodyEntries);

  const bodyCacheFlagsSingle = useInboxBodyCacheFlags(
    useMultiBodyFlags || useMultiAccountBody ? undefined : accountId,
    folder ?? "INBOX",
    listUids,
    listBodyCached,
    priorityBodyUids
  );

  const bodyCacheFlagsMulti = useMultiFolderBodyCacheFlags(
    useMultiBodyFlags ? accountId : undefined,
    bodyCacheEntries
  );

  const resolveBodyCacheStatus = useCallback(
    (msg: MailMessageSummary) => {
      if (useMultiAccountBody) {
        const aid = msg.accountId ?? accountIdForMessage?.(msg);
        if (aid != null) {
          return bodyCacheFlagsMultiAccount.get(unifiedMessageKey(aid, msg.uid)) ?? "idle";
        }
      }
      if (useMultiBodyFlags) {
        return bodyCacheFlagsMulti.get(msg.uid) ?? "idle";
      }
      return bodyCacheFlagsSingle.get(msg.uid) ?? "idle";
    },
    [
      accountIdForMessage,
      bodyCacheFlagsMulti,
      bodyCacheFlagsMultiAccount,
      bodyCacheFlagsSingle,
      useMultiAccountBody,
      useMultiBodyFlags,
    ]
  );

  /** Prêt à afficher : DD02 (bodyCached) **ou** mémoire client. */
  const isClientBodyReady = useCallback(
    (msg: MailMessageSummary) => {
      if (Boolean(msg.bodyCached)) return true;
      const aid = msg.accountId ?? accountIdForMessage?.(msg) ?? accountId;
      if (aid == null || msg.uid <= 0) return false;
      const bodyFolder = bodyFolderByUid?.get(msg.uid) ?? msg.imapFolder ?? folder ?? "INBOX";
      const bodyUid = bodyUidByUid?.get(msg.uid) ?? msg.imapBodyUid ?? msg.uid;
      return isMessageBodyCached(aid, bodyFolder, bodyUid);
    },
    [accountId, accountIdForMessage, bodyFolderByUid, bodyUidByUid, folder]
  );

  const primeArrivalBody = useCallback(
    (msg: MailMessageSummary) => {
      const aid = msg.accountId ?? accountIdForMessage?.(msg) ?? accountId;
      if (aid == null || msg.uid <= 0) return;
      const bodyFolder = bodyFolderByUid?.get(msg.uid) ?? msg.imapFolder ?? folder ?? "INBOX";
      const bodyUid = bodyUidByUid?.get(msg.uid) ?? msg.imapBodyUid ?? msg.uid;
      // Warm DD02 uniquement — ouverture = lecture serveur, pas hydrate IndexedDB.
      queueWarmMessageBodies(aid, bodyFolder, [bodyUid], { newMail: true });
    },
    [accountId, accountIdForMessage, bodyFolderByUid, bodyUidByUid, folder]
  );

  /** Masqué → hydrate mémoire (IDB puis DD02) avant révélation. */
  const hydrateRevealBody = useCallback(
    (msg: MailMessageSummary) => {
      const aid = msg.accountId ?? accountIdForMessage?.(msg) ?? accountId;
      if (aid == null || msg.uid <= 0) return;
      const bodyFolder = bodyFolderByUid?.get(msg.uid) ?? msg.imapFolder ?? folder ?? "INBOX";
      const bodyUid = bodyUidByUid?.get(msg.uid) ?? msg.imapBodyUid ?? msg.uid;
      void warmMessageBodyForOpen(aid, bodyFolder, bodyUid, msg);
    },
    [accountId, accountIdForMessage, bodyFolderByUid, bodyUidByUid, folder]
  );

  const clientReadyRevision = useMemo(() => {
    let localReady = 0;
    let warming = 0;
    for (const msg of messages) {
      if (msg.uid <= 0) continue;
      const aid = msg.accountId ?? accountIdForMessage?.(msg) ?? accountId;
      const bodyFolder = bodyFolderByUid?.get(msg.uid) ?? msg.imapFolder ?? folder ?? "INBOX";
      const bodyUid = bodyUidByUid?.get(msg.uid) ?? msg.imapBodyUid ?? msg.uid;
      if (
        Boolean(msg.bodyCached) ||
        (aid != null && isMessageBodyCached(aid, bodyFolder, bodyUid))
      ) {
        localReady += 1;
      }
      const status = resolveBodyCacheStatus(msg);
      if (status === "warming") warming += 1;
    }
    return `${localReady}:${warming}:${bodyCacheFlagsSingle.size}:${bodyCacheFlagsMulti.size}:${bodyCacheFlagsMultiAccount.size}`;
  }, [
    accountId,
    accountIdForMessage,
    bodyCacheFlagsMulti,
    bodyCacheFlagsMultiAccount,
    bodyCacheFlagsSingle,
    bodyFolderByUid,
    bodyUidByUid,
    folder,
    messages,
    resolveBodyCacheStatus,
  ]);

  const preparingEnabled = !isDraftsView && !isSentView && !isOutboxFolder;
  const listSettled =
    preparingEnabled &&
    (messages.length > 0 || (!loadingInitial && !syncing && !initialLoading));

  const { visibleMessages } = useInboxPreparingMessages(messages, {
    enabled: preparingEnabled,
    scopeKey: useMultiAccountBody
      ? `unified:${folder}`
      : `${accountId ?? "none"}:${folder ?? "INBOX"}`,
    listSettled,
    accountId,
    folder: folder ?? "INBOX",
    isClientReady: isClientBodyReady,
    clientReadyRevision,
    onPrimeArrival: primeArrivalBody,
    onRevealReady: hydrateRevealBody,
    accountIdForMessage,
  });

  // Fond : ~10 visibles bodyCached, **seulement UIDs pas encore tentés** (doc §4.1).
  const hydrateFingerprint = useMemo(() => {
    const hydrateListReady =
      !isOutboxFolder && messages.length > 0 && (listSettled || isSentView || isDraftsView);
    if (!hydrateListReady) return "";
    const parts: string[] = [];
    for (const msg of messages) {
      if (msg.uid <= 0) continue;
      const status = resolveBodyCacheStatus(msg);
      if (msg.bodyCached !== true && status !== "ready") continue;
      const aid = msg.accountId ?? accountIdForMessage?.(msg) ?? accountId;
      if (aid == null) continue;
      const listFolder = folder ?? "INBOX";
      if (isClientHydrateSettled(aid, listFolder, msg.uid)) continue;
      parts.push(`${aid}:${listFolder}:${msg.uid}`);
      if (parts.length >= INBOX_CLIENT_HYDRATE_MAX) break;
    }
    return parts.join("|");
  }, [
    accountId,
    accountIdForMessage,
    clientReadyRevision,
    folder,
    isDraftsView,
    isOutboxFolder,
    isSentView,
    listSettled,
    messages,
    resolveBodyCacheStatus,
  ]);

  useEffect(() => {
    if (!hydrateFingerprint) return;
    const listFolder = folder ?? "INBOX";
    const entries = messages
      .filter((msg) => {
        if (msg.uid <= 0) return false;
        const status = resolveBodyCacheStatus(msg);
        if (msg.bodyCached !== true && status !== "ready") return false;
        const aid = msg.accountId ?? accountIdForMessage?.(msg) ?? accountId;
        if (aid == null) return false;
        return !isClientHydrateSettled(aid, listFolder, msg.uid);
      })
      .slice(0, INBOX_CLIENT_HYDRATE_MAX)
      .map((msg) => {
        const aid = msg.accountId ?? accountIdForMessage?.(msg) ?? accountId;
        if (aid == null) return null;
        return {
          accountId: aid,
          folder: listFolder,
          uid: msg.uid,
          priority: msg.seen === false,
        };
      })
      .filter((e): e is NonNullable<typeof e> => e != null);
    if (entries.length === 0) return;
    const hydrateScope = useMultiAccountBody
      ? `unified:${listFolder}`
      : `${accountId ?? "none"}:${listFolder}`;
    schedulePrimeVisibleReadyBodies(entries, INBOX_CLIENT_HYDRATE_MAX, hydrateScope);
  }, [
    accountId,
    accountIdForMessage,
    folder,
    hydrateFingerprint,
    resolveBodyCacheStatus,
    useMultiAccountBody,
  ]);

  const filter = listFilter ?? {
    unreadOnly: false,
    importantOnly: false,
    favoriteOnly: false,
    attachmentsOnly: false,
    colorLabel: null,
  };
  const filterActive = inboxFilterIsActive(filter);
  const clientFilterOnly =
    filter.importantOnly ||
    filter.favoriteOnly ||
    filter.attachmentsOnly ||
    filter.colorLabel != null;

  const headerActionBtn =
    "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[var(--mail-text)] transition hover:bg-[var(--mail-surface-hover)] disabled:pointer-events-none disabled:opacity-35";

  const statsLine = navigationOpen
    ? showSearchHint
      ? searchDebouncePending
        ? searchLocalCount > 0
          ? `${searchLocalCount} résultat${searchLocalCount > 1 ? "s" : ""} récent${searchLocalCount > 1 ? "s" : ""} · « ${searchQuery.trim()} »`
          : `Filtrage local… · « ${searchQuery.trim()} »`
        : searchImapPending
          ? searchLocalCount > 0
            ? `${messages.length} affiché${messages.length > 1 ? "s" : ""} · recherche IMAP… · « ${searchCommittedQuery.trim() || searchQuery.trim()} »`
            : `Recherche dans toute la boîte… · « ${searchCommittedQuery.trim() || searchQuery.trim()} »`
          : `${messages.length} résultat${messages.length > 1 ? "s" : ""} · « ${searchCommittedQuery.trim() || searchQuery.trim()} »`
      : clientFilterOnly
        ? `${messages.length} filtré${messages.length > 1 ? "s" : ""} · ${messages.length + hiddenByFilterCount}/${total} chargés`
        : filter.unreadOnly
          ? `${messages.length} non lu${messages.length > 1 ? "s" : ""}`
          : filterActive
            ? `${messages.length} filtré${messages.length > 1 ? "s" : ""}`
            : `${messages.length}/${total} message${total > 1 ? "s" : ""}`
    : null;

  const statsSuffix = navigationOpen
    ? `${searchImapPending ? " · serveur IMAP" : ""}${!showSearchHint && backgroundSync ? " · sync…" : ""}`
    : "";

  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--mail-bg)]/40">
      <div className="shrink-0 border-b border-[var(--mail-border-soft)] px-4 pb-3 pt-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-1.5">
              {onOpenNav ? (
                <button
                  type="button"
                  onClick={onOpenNav}
                  className="-ml-1 rounded-md p-1 text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)] md:hidden"
                  aria-label="Comptes et dossiers"
                >
                  <Menu className="h-4 w-4" />
                </button>
              ) : null}
              <h2 className="min-w-0 truncate text-base font-semibold text-[var(--mail-text)]">
                {navigationOpen ? folderLabel : "Messages"}
              </h2>
            </div>
            {statsLine ? (
              <p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[11px] text-[var(--mail-muted)]">
                {searchImapPending ? (
                  <Loader2 className="h-3 w-3 shrink-0 animate-spin text-[var(--mail-accent)]" aria-hidden />
                ) : null}
                <span className="truncate">
                  {statsLine}
                  {statsSuffix}
                </span>
              </p>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-1 pt-0.5">
            {onOpenFolderStorage && accountId != null && !isDraftsView && !useMultiAccountBody ? (
              <MailTooltip
                compact
                side="bottom"
                label={
                  folderStorageActive
                    ? "Copie en cours pour ce dossier…"
                    : "Copier jusqu'à 50 e-mails de ce dossier sur le serveur"
                }
              >
                <button
                  type="button"
                  onClick={onOpenFolderStorage}
                  disabled={!navigationOpen || initialLoading || folderStorageActive}
                  className={cn(
                    "rounded-full p-1 text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)] disabled:opacity-40",
                    folderStorageActive && "text-[var(--mail-accent)]"
                  )}
                  aria-label="Copier ce dossier sur le serveur"
                  aria-busy={folderStorageActive}
                >
                  <HardDrive className={cn("h-3.5 w-3.5", folderStorageActive && "animate-pulse")} />
                </button>
              </MailTooltip>
            ) : null}
            <button
              type="button"
              onClick={() => onRefresh()}
              className={cn(
                "rounded-full p-1 text-[var(--mail-muted)] transition hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)]",
                (backgroundSync || syncing) && "text-[var(--mail-accent)]"
              )}
              aria-label="Actualiser les messages"
              aria-busy={syncing}
            >
              <RefreshCw className={cn("h-3.5 w-3.5", syncing && "animate-spin")} />
            </button>
          </div>
        </div>

        <div className="mt-1 flex min-h-8 min-w-0 flex-nowrap items-center gap-1 overflow-hidden">
          {bulkActive ? (
            <>
              <span className="shrink-0 text-[11px] text-[var(--mail-muted)] tabular-nums">
                {bulkCount} sél.
              </span>
              <div className="flex shrink-0 items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => onBulkAction?.("markRead")}
                  className={headerActionBtn}
                  aria-label="Marquer comme lu"
                >
                  <MailOpen className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => onBulkAction?.("markUnread")}
                  className={headerActionBtn}
                  aria-label="Marquer comme non lu"
                >
                  <Mail className="h-4 w-4" />
                </button>
                {!hideBulkMove ? (
                  <button
                    type="button"
                    onClick={() => onBulkAction?.("move")}
                    className={headerActionBtn}
                    aria-label="Déplacer"
                  >
                    <FolderInput className="h-4 w-4" />
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => onBulkAction?.("delete")}
                  className={cn(
                    headerActionBtn,
                    "text-[var(--mail-danger-text)] hover:bg-[var(--mail-danger-soft)]"
                  )}
                  aria-label="Supprimer"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
                {showCheckboxes ? (
                  <>
                    <button
                      type="button"
                      onClick={() => onBulkAction?.("selectWithAttachments")}
                      className={headerActionBtn}
                      aria-label="Sélectionner les messages avec PJ"
                    >
                      <Paperclip className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onBulkAction?.("downloadAttachments")}
                      className={headerActionBtn}
                      aria-label="Télécharger les pièces jointes"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onBulkAction?.("saveToCoffre")}
                      className={headerActionBtn}
                      aria-label="Enregistrer dans Coffre"
                    >
                      <FolderLock className="h-4 w-4" />
                    </button>
                  </>
                ) : null}
              </div>
              {onListFilterChange ? (
                <>
                  <InboxListFilterSelect
                    filter={filter}
                    disabled={!navigationOpen}
                    onChange={(next) => onListFilterChange(next)}
                  />
                  <button
                    type="button"
                    onClick={() => onClearSelection?.()}
                    className={headerActionBtn}
                    aria-label="Tout désélectionner"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </>
              ) : null}
            </>
          ) : (
            <>
              {navigationOpen && (listSubtitle ?? accountEmail) ? (
                <p className="min-w-0 flex-1 truncate text-xs text-[var(--mail-muted)]">
                  {listSubtitle ?? accountEmail}
                </p>
              ) : (
                <span className="min-w-0 flex-1" aria-hidden />
              )}
              {onListFilterChange ? (
                <InboxListFilterSelect
                  filter={filter}
                  disabled={!navigationOpen}
                  onChange={(next) => onListFilterChange(next)}
                />
              ) : null}
            </>
          )}
        </div>
      </div>

      <div className="relative min-h-0 flex-1 overflow-y-auto py-1">
        {!navigationOpen ? (
          <p className="px-4 py-10 text-center text-sm text-[var(--mail-muted)]">
            Ouvrez un compte et un dossier pour afficher les messages.
          </p>
        ) : null}

        {navigationOpen && searchImapPending && messages.length === 0 ? (
          <MailLoadingState variant="search" layout="compact" />
        ) : null}

        {navigationOpen && initialLoading && messages.length === 0 && !searchImapPending ? (
          <MailLoadingState variant="messages" layout="compact" />
        ) : null}

        {navigationOpen && error ? (
          <p className="mx-2 my-2 mail-alert-error px-4 py-3 text-sm" role="alert">
            {error}
          </p>
        ) : null}

        {navigationOpen && searchImapPending && messages.length > 0 ? (
          <div
            className="mx-2 mb-1 overflow-hidden rounded-lg border border-[var(--mail-accent)]/20 bg-[var(--mail-accent-soft)] px-3 py-2"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <div className="flex items-center justify-center gap-2 text-xs font-medium text-[var(--mail-accent)]">
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden />
              <span>Recherche IMAP en cours dans toute la boîte…</span>
            </div>
            <div className="mt-1.5 h-0.5 overflow-hidden rounded-full bg-[var(--mail-border-soft)]">
              <div className="inbox-load-progress-indeterminate h-full w-2/5 rounded-full bg-[var(--mail-accent)]" />
            </div>
          </div>
        ) : null}

        {navigationOpen &&
        !error &&
        searchShowEmptyResults &&
        messages.length === 0 &&
        !searchImapPending &&
        !initialLoading ? (
          <p className="px-4 py-10 text-center text-sm text-[var(--mail-muted)]">
            Aucun message ne correspond à la recherche.
          </p>
        ) : null}

        {navigationOpen &&
        !error &&
        messages.length === 0 &&
        !searchImapPending &&
        !searchDebouncePending &&
        !searchShowEmptyResults &&
        !initialLoading &&
        !loadingInitial &&
        !syncing ? (
          <p className="px-4 py-10 text-center text-sm text-[var(--mail-muted)]">
            {filterActive && hiddenByFilterCount > 0
              ? `${hiddenByFilterCount} message${hiddenByFilterCount > 1 ? "s" : ""} masqué${hiddenByFilterCount > 1 ? "s" : ""} par le filtre — désactivez-le pour les afficher.`
              : filterActive
                ? "Aucun message ne correspond au filtre."
                : isOutboxFolder
                  ? "Aucun envoi programmé ni en attente d'envoi."
                  : "Aucun message dans ce dossier."}
          </p>
        ) : null}

        {navigationOpen
          ? visibleMessages.map((msg) => {
              const rowAccountId = accountIdForMessage?.(msg) ?? accountId;
              const rawBodyStatus = resolveBodyCacheStatus(msg);
              const bodyFolder = bodyFolderByUid?.get(msg.uid) ?? msg.imapFolder ?? folder ?? "INBOX";
              const bodyUid = bodyUidByUid?.get(msg.uid) ?? msg.imapBodyUid ?? msg.uid;
              /** Pastille = statut réactif du hook (évite « invisible jusqu’au survol »). */
              const bodyStatus = rawBodyStatus;
              const showBadge =
                rowAccountId != null &&
                msg.uid > 0 &&
                msg.sendStatus == null &&
                bodyStatus !== "warming";
              const bodyOpenable =
                isDraftsView || !requireBodyReady || isBodyCacheOpenable(bodyStatus);
              const snoozeUntilLabel = snoozeUntilByUid?.get(msg.uid);
              const rowKey = msg.accountId != null ? `${msg.accountId}:${msg.uid}` : String(msg.outboxId ?? msg.uid);
              const selected = isRowSelected ? isRowSelected(msg) : selectedUid === msg.uid;
              const checked = isRowChecked ? isRowChecked(msg) : (checkedUids?.has(msg.uid) ?? false);
              const contextMenuActive = isContextMenuRow
                ? isContextMenuRow(msg)
                : contextMenuUid === msg.uid;
              return (
                <InboxMessageRowExitWrap
                  key={rowKey}
                  exiting={exitingUids?.has(msg.uid) ?? false}
                >
                  <InboxMessageRow
                  subject={msg.subject}
                  from={msg.from}
                  to={msg.to}
                  preferRecipient={isSentView || isDraftsView}
                  date={msg.date}
                  seen={msg.seen}
                  flagged={msg.flagged}
                  favorite={msg.favorite}
                  colorLabel={msg.colorLabel ?? null}
                  hasAttachments={msg.hasAttachments}
                  sendStatus={msg.sendStatus}
                  sendTo={msg.sendTo}
                  sendScheduledAtLabel={
                    msg.sendScheduledAt ? formatSnoozeUntil(msg.sendScheduledAt) : undefined
                  }
                  selected={selected}
                  contextMenuActive={contextMenuActive}
                  checked={checked}
                  showCheckbox={showCheckboxes && msg.sendStatus == null}
                  showBodyCacheBadge={showBadge}
                  bodyCacheStatus={bodyStatus}
                  bodyOpenable={bodyOpenable}
                  draftDeleting={msg.draftDeleting === true}
                  accountLabel={accountLabelForMessage?.(msg)}
                  snoozeUntilLabel={
                    snoozeUntilLabel ? formatSnoozeUntil(snoozeUntilLabel) : undefined
                  }
                  onHover={() => {
                    if (rowAccountId != null) {
                      prefetchInboxBodyIfCached(rowAccountId, bodyFolder, bodyUid, rawBodyStatus);
                      onPrefetchMessage?.(msg.uid);
                    }
                  }}
                  onToggleCheck={(event) => onToggleCheck?.(msg.uid, event, msg.accountId)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    onContextMenu?.(e, msg.uid, msg.accountId);
                  }}
                  onSelect={(event) => {
                    if (rowAccountId == null || !bodyOpenable) {
                      onSelectMessage(msg.uid, event, {
                        bodyBlocked: !bodyOpenable,
                        accountId: msg.accountId,
                      });
                      return;
                    }

                    // Ouvrir tout de suite — jamais attendre le réseau au clic.
                    onSelectMessage(msg.uid, event, {
                      bodyBlocked: false,
                      accountId: msg.accountId,
                    });
                  }}
                />
                </InboxMessageRowExitWrap>
              );
            })
          : null}

        {navigationOpen && hasMore ? (
          <div className="px-3 py-3">
            <button
              type="button"
              onClick={() => onLoadMore()}
              disabled={loadingMore}
              className="w-full rounded-full border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 py-2.5 text-xs font-medium text-[var(--mail-accent)] shadow-[var(--mail-shadow-sm)] transition hover:shadow-[var(--mail-shadow-md)] disabled:opacity-50"
            >
              {loadingMore ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <MailCircularProgress size={16} className="[&_p]:hidden" />
                  Chargement…
                </span>
              ) : (
                "Afficher les messages plus anciens"
              )}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
